import { Injectable, Signal, computed, signal } from '@angular/core';

export const DESKTOP_SIDEBAR_EXPANDED_STORAGE_KEY = 'Proffeo_desktopSidebarExpanded';

/**
 * DesktopSidebarPreferencesService - Persists manual desktop sidebar open/closed state.
 *
 * Scope: Global (`providedIn: 'root'`).
 *
 * Usage: Used by {@link LayoutComponent} to restore pinned sidebar width. Hover peek
 * is enabled only when the user prefers the sidebar collapsed; pinning it open
 * disables hover auto-expand/collapse.
 */
@Injectable({ providedIn: 'root' })
export class DesktopSidebarPreferencesService {
  private readonly expandedState = signal<boolean | null>(null);
  private readonly manualPreferenceState = signal(false);

  public readonly expanded = this.expandedState.asReadonly();
  public readonly hasManualPreference: Signal<boolean> = this.manualPreferenceState.asReadonly();
  /** True only when preferred collapsed so the rail can temporarily expand on hover. */
  public readonly isHoverBehaviorEnabled = computed(() => this.expandedState() === false);

  public constructor() {
    this.loadFromStorage();
  }

  public setExpanded(expanded: boolean): void {
    this.expandedState.set(expanded);
    this.manualPreferenceState.set(true);
    this.syncStorage();
  }

  private loadFromStorage(): void {
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }

    const storedValue = window.localStorage.getItem(DESKTOP_SIDEBAR_EXPANDED_STORAGE_KEY);
    if (storedValue === null) {
      return;
    }

    this.expandedState.set(storedValue === 'true');
    this.manualPreferenceState.set(true);
  }

  private syncStorage(): void {
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }

    const expanded = this.expandedState();
    if (expanded === null) {
      window.localStorage.removeItem(DESKTOP_SIDEBAR_EXPANDED_STORAGE_KEY);
      return;
    }

    window.localStorage.setItem(DESKTOP_SIDEBAR_EXPANDED_STORAGE_KEY, expanded ? 'true' : 'false');
  }
}
